import { useState, useEffect } from "react";
import { Search, X, Plus, Phone, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Label } from "@/components/ui/label";
import { PhoneInput } from "@/components/ui/phone-input";
import { SearchableSelect } from "@/components/ui/searchable-select";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { getAvatarColor } from "@/lib/utils";
import { useLeadSearch, type LeadSearchResult } from "@/hooks/leads/useLeadSearch";
import { useCreateLead } from "@/hooks/leads/useLeads";
import { useOpcoesDePessoas } from "@/hooks/admin/useOpcoesDePessoas";
import { useCurrentUser } from "@/hooks/auth";
import { toast } from "@/hooks/use-toast";

interface LeadStepProps {
  selectedLead: LeadSearchResult | null;
  onSelect: (lead: LeadSearchResult | null) => void;
}

function getInitials(name: string) {
  return name
    .split(" ")
    .slice(0, 2)
    .map((w) => w.charAt(0))
    .join("")
    .toUpperCase();
}

export function LeadStep({ selectedLead, onSelect }: LeadStepProps) {
  const [open, setOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const { leads, isLoading, query, setQuery } = useLeadSearch();

  if (selectedLead) {
    return (
      <div className="space-y-3">
        <div className="rounded-lg border bg-card p-3 flex items-center gap-3">
          <div
            className="h-9 w-9 rounded-full flex items-center justify-center text-xs font-semibold text-white flex-shrink-0"
            style={{ background: getAvatarColor(selectedLead.name) }}
          >
            {getInitials(selectedLead.name)}
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium truncate">{selectedLead.name}</p>
            <p className="text-2xs text-muted-foreground truncate">
              {selectedLead.email || selectedLead.phone || "Sem contato"}
            </p>
          </div>
          {selectedLead.companies?.name && (
            <Badge variant="secondary" className="text-2xs flex-shrink-0">
              {selectedLead.companies.name}
            </Badge>
          )}
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => onSelect(null)}
            aria-label="Trocar lead selecionado"
          >
            Trocar
          </Button>
        </div>
      </div>
    );
  }

  if (creating) {
    return (
      <QuickCreateLead
        onCancel={() => setCreating(false)}
        onCreated={(lead) => {
          setCreating(false);
          onSelect(lead);
        }}
      />
    );
  }

  return (
    <div className="space-y-3">
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            type="button"
            variant="outline"
            role="combobox"
            aria-expanded={open}
            className="w-full justify-start text-left font-normal h-10 text-muted-foreground"
          >
            <Search className="mr-2 h-4 w-4 flex-shrink-0" />
            Buscar lead por nome ou email...
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-[var(--radix-popover-trigger-width)] p-0" align="start">
          <Command shouldFilter={false}>
            <CommandInput placeholder="Buscar lead..." value={query} onValueChange={setQuery} />
            <CommandList>
              <CommandEmpty>
                {isLoading
                  ? "Buscando..."
                  : query.length >= 2
                    ? `Nenhum lead encontrado para "${query}"`
                    : "Digite para buscar leads..."}
              </CommandEmpty>
              <CommandGroup heading={query.length < 2 ? "Recentes" : "Resultados"}>
                {leads.map((lead) => (
                  <CommandItem
                    key={lead.id}
                    value={lead.id}
                    onSelect={() => {
                      onSelect(lead);
                      setOpen(false);
                      setQuery("");
                    }}
                    className="flex items-center gap-3 py-2.5"
                  >
                    <div
                      className="h-7 w-7 rounded-full flex items-center justify-center text-2xs font-semibold text-white flex-shrink-0"
                      style={{ background: getAvatarColor(lead.name) }}
                    >
                      {getInitials(lead.name)}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium truncate">{lead.name}</p>
                      <p className="text-2xs text-muted-foreground truncate">{lead.email}</p>
                    </div>
                    {lead.companies?.name && (
                      <Badge variant="secondary" className="text-2xs flex-shrink-0">
                        {lead.companies.name}
                      </Badge>
                    )}
                  </CommandItem>
                ))}
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>

      <div className="flex items-center gap-3">
        <div className="h-px flex-1 bg-border" />
        <span className="text-2xs text-muted-foreground uppercase tracking-wide">ou</span>
        <div className="h-px flex-1 bg-border" />
      </div>

      <Button type="button" variant="outline" className="w-full gap-2" onClick={() => setCreating(true)}>
        <Plus className="h-4 w-4" />
        Criar novo lead
      </Button>
    </div>
  );
}

function QuickCreateLead({
  onCancel,
  onCreated,
}: {
  onCancel: () => void;
  onCreated: (lead: LeadSearchResult) => void;
}) {
  const { opcoes: pessoas } = useOpcoesDePessoas();
  const { user: currentUser } = useCurrentUser();

  const [name, setName] = useState("");
  const [ddi, setDdi] = useState("55");
  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");
  const [ownerId, setOwnerId] = useState<string>("");
  const [error, setError] = useState<string | null>(null);

  // Se o lead digitado já existe na org, o insert colide com `unique_lead_per_org_idx`.
  // Reaproveita o lead existente em vez de travar o wizard.
  const createLead = useCreateLead({
    onDuplicate: (existing) => {
      if (!existing) {
        setError("Já existe um lead com esses dados nesta organização. Busque por ele acima.");
        return;
      }
      toast({
        title: "Lead já existia",
        description: `${existing.name || "Esse lead"} já estava cadastrado e foi selecionado.`,
      });
      onCreated({
        id: existing.id,
        name: existing.name ?? "",
        email: existing.email ?? "",
        phone: existing.phone ?? null,
        company_id: existing.company_id ?? null,
        companies: null,
      });
    },
  });

  useEffect(() => {
    if (currentUser?.user_id && !ownerId) {
      setOwnerId(currentUser.user_id);
    }
  }, [currentUser, ownerId]);

  const handleCreate = async () => {
    if (!name.trim()) {
      setError("Nome é obrigatório");
      return;
    }
    setError(null);
    try {
      const created = await createLead.mutateAsync({
        name: name.trim(),
        email: email.trim() || undefined,
        phone: phone ? `${ddi || "55"}${phone}` : undefined,
        owner_id: ownerId || undefined,
      });
      onCreated({
        id: created.id,
        name: created.name,
        email: created.email ?? "",
        phone: created.phone ?? null,
        company_id: created.company_id ?? null,
        companies: null,
      });
    } catch {
      // toast tratado pelo hook
    }
  };

  return (
    <div className="space-y-3 rounded-lg border bg-muted/30 p-4">
      <div className="flex items-center justify-between">
        <h5 className="text-sm font-semibold">Novo lead</h5>
        <Button type="button" variant="ghost" size="icon-sm" onClick={onCancel} aria-label="Cancelar criação">
          <X className="h-4 w-4" />
        </Button>
      </div>

      <div className="space-y-2">
        <Label htmlFor="quick-lead-name">Nome *</Label>
        <Input
          id="quick-lead-name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="Nome completo"
          autoFocus
        />
        {error && <p className="text-2xs text-destructive">{error}</p>}
      </div>

      <div className="space-y-2">
        <Label>Telefone</Label>
        <div className="flex">
          <div className="flex items-center h-10 border border-input bg-input-background rounded-l-md px-2 gap-1 border-r-0">
            <Phone className="text-muted-foreground h-4 w-4 shrink-0" />
            <span className="text-muted-foreground text-sm select-none">+</span>
            <input
              type="text"
              maxLength={4}
              placeholder="55"
              className="w-9 bg-transparent border-none outline-none text-sm p-0"
              value={ddi}
              onChange={(e) => setDdi(e.target.value.replace(/\D/g, "").slice(0, 4))}
            />
          </div>
          <PhoneInput className="rounded-l-none" value={phone} onRawChange={setPhone} />
        </div>
      </div>

      <div className="space-y-2">
        <Label htmlFor="quick-lead-email">Email</Label>
        <Input
          id="quick-lead-email"
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="email@empresa.com"
        />
      </div>

      <div className="space-y-2">
        <Label>Proprietário</Label>
        <SearchableSelect
          value={ownerId || "__none__"}
          onValueChange={(v) => setOwnerId(v === "__none__" ? "" : v)}
          placeholder="Selecione o proprietário"
          options={[
            { value: "__none__", label: "Sem proprietário" },
            ...pessoas,
          ]}
        />
      </div>

      <div className="flex justify-end gap-2 pt-1">
        <Button type="button" variant="ghost" onClick={onCancel}>
          Cancelar
        </Button>
        <Button type="button" onClick={handleCreate} disabled={createLead.isPending} className="gap-2">
          {createLead.isPending && <Loader2 className="h-4 w-4 animate-spin" />}
          Criar e selecionar
        </Button>
      </div>
    </div>
  );
}
